import { useGlobalToast } from 'transfix-ui/components/Toast'
import {
  IEdiMessage,
  ICreateInboundEdiMessageInput,
  IUpdateInboundEdiMessageInput,
  ICreateOutboundEdiMessageInput,
  IUpdateOutboundEdiMessageInput,
} from 'types/graphqlTypes'
import {
  encodeOptionValue as encodeTransactionSetOptionValue,
  decodeOptionValue as decodeTransactionSetOptionValue,
} from '../../../EdiSelfServiceOutbound/hooks/useTransactionSets'
import {
  TConnectionConfigType,
  TEdiMessageTypeSchema,
} from '../forms/EdiMessageTypeForm/EdiMessageTypeSchema'
import { useCreateInboundEdiMessageTypeMutation } from '../graphql/CreateInboundEdiMessageType'
import { useCreateOutboundEdiMessageTypeMutation } from '../graphql/CreateOutboundEdiMessageType'
import { useUpdateInboundEdiMessageTypeMutation } from '../graphql/UpdateInboundEdiMessageType'
import { useUpdateOutboundEdiMessageTypeMutation } from '../graphql/UpdateOutboundEdiMessageType'

export const useEdiMessageTypeMutations = () => {
  const { openToast } = useGlobalToast()

  const [_createInboundEdiMessageType] = useCreateInboundEdiMessageTypeMutation()
  const [_updateInboundEdiMessageType] = useUpdateInboundEdiMessageTypeMutation()
  const [_createOutboundEdiMessageType] = useCreateOutboundEdiMessageTypeMutation()
  const [_updateOutboundEdiMessageType] = useUpdateOutboundEdiMessageTypeMutation()

  const createInboundEdiMessageType = async (input: ICreateInboundEdiMessageInput) => {
    try {
      const response = await _createInboundEdiMessageType({
        variables: {
          input,
        },
        refetchQueries: ['EdiMessageTypes'],
      })

      const { data } = response || {}
      const { createInboundEdiMessageType } = data || {}
      const { errors = [] } = createInboundEdiMessageType || {}
      const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

      if (filteredErrors.length > 0) {
        openToast({
          toastMessage: filteredErrors.join(', '),
          toastType: 'alert',
          toastDuration: null,
        })
        return
      }

      openToast({
        toastMessage: 'Message type created successfully.',
        toastType: 'default',
      })
    } catch (e) {
      openToast({ toastMessage: e.message, toastType: 'alert' })
    }
  }

  const updateInboundEdiMessageType = async (input: IUpdateInboundEdiMessageInput) => {
    try {
      const response = await _updateInboundEdiMessageType({
        variables: {
          input,
        },
        refetchQueries: ['EdiMessageTypes'],
      })

      const { data } = response || {}
      const { updateInboundEdiMessageType } = data || {}
      const { errors = [] } = updateInboundEdiMessageType || {}
      const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

      if (filteredErrors.length > 0) {
        openToast({
          toastMessage: filteredErrors.join(', '),
          toastType: 'alert',
          toastDuration: null,
        })
        return
      }

      openToast({
        toastMessage: 'Message type updated successfully.',
        toastType: 'default',
      })
    } catch (e) {
      openToast({ toastMessage: e.message, toastType: 'alert' })
    }
  }

  const createOutboundEdiMessageType = async (input: ICreateOutboundEdiMessageInput) => {
    try {
      const response = await _createOutboundEdiMessageType({
        variables: {
          input,
        },
        refetchQueries: ['EdiMessageTypes'],
      })

      const { data } = response || {}
      const { createOutboundEdiMessageType } = data || {}
      const { errors = [] } = createOutboundEdiMessageType || {}
      const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

      if (filteredErrors.length > 0) {
        openToast({
          toastMessage: filteredErrors.join(', '),
          toastType: 'alert',
          toastDuration: null,
        })
        return
      }

      openToast({
        toastMessage: 'Message type created successfully.',
        toastType: 'default',
      })
    } catch (e) {
      openToast({ toastMessage: e.message, toastType: 'alert' })
    }
  }

  const updateOutboundEdiMessageType = async (input: IUpdateOutboundEdiMessageInput) => {
    try {
      const response = await _updateOutboundEdiMessageType({
        variables: {
          input,
        },
        refetchQueries: ['EdiMessageTypes'],
      })

      const { data } = response || {}
      const { updateOutboundEdiMessageType } = data || {}
      const { errors = [] } = updateOutboundEdiMessageType || {}
      const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

      if (filteredErrors.length > 0) {
        openToast({
          toastMessage: filteredErrors.join(', '),
          toastType: 'alert',
          toastDuration: null,
        })
        return
      }

      openToast({
        toastMessage: 'Message type updated successfully.',
        toastType: 'default',
      })
    } catch (e) {
      openToast({ toastMessage: e.message, toastType: 'alert' })
    }
  }

  // convert GQL data into form parameters
  const buildFormValues = ({
    connectionConfigId,
    connectionConfigType,
    ediMessageType,
  }: {
    ediMessageType?: IEdiMessage | null
    connectionConfigType: TConnectionConfigType
    connectionConfigId: string
  }): TEdiMessageTypeSchema => {
    return {
      id: ediMessageType?.id || null,
      connectionConfigType,
      connectionConfigId,
      transactionSetWithSubtype: encodeTransactionSetOptionValue(
        ediMessageType?.transactionSet,
        ediMessageType?.transactionSetSubtype
      ),
      ediMapperConfigurableParserId: ediMessageType?.ediMapperConfigurableParserId || '',
      ediMapperConfigurableBuilderId: ediMessageType?.ediMapperConfigurableBuilderId || '',
      legacyParserClass: ediMessageType?.legacyParserClass || '',
      legacyBuilderClass: ediMessageType?.legacyBuilderClass || '',
    }
  }

  // accept form params and persist to backend
  //
  // 'building mutation payload' and 'calling the mutation' are mixed here because we
  // need to do both conditionally (decide which mutation to call, and then prepare the correct payload for that mutation)
  const persistEdiMessageType = (formParams: TEdiMessageTypeSchema) => {
    const { connectionConfigType } = formParams
    const { transactionSet, transactionSetSubtype = null } = decodeTransactionSetOptionValue(
      formParams.transactionSetWithSubtype
    )

    if (connectionConfigType === 'InboundConnectionConfig') {
      const commonInboundParams = {
        transactionSet,
        transactionSetSubtype,
        ediMapperConfigurableParserId:
          formParams.ediMapperConfigurableParserId === ''
            ? null
            : formParams.ediMapperConfigurableParserId,
        legacyParserClass:
          formParams.legacyParserClass === '' ? null : formParams.legacyParserClass,
      }

      if (formParams.id) {
        updateInboundEdiMessageType({ ...commonInboundParams, id: formParams.id })
      } else {
        createInboundEdiMessageType({
          ...commonInboundParams,
          inboundEdiMapperConnectionConfigId: formParams.connectionConfigId,
        })
      }
    } else if (connectionConfigType === 'OutboundConnectionConfig') {
      const commonOutboundParams = {
        transactionSet,
        transactionSetSubtype,
        ediMapperConfigurableBuilderId:
          formParams.ediMapperConfigurableBuilderId === ''
            ? null
            : formParams.ediMapperConfigurableBuilderId,
        legacyBuilderClass:
          formParams.legacyBuilderClass === '' ? null : formParams.legacyBuilderClass,
      }

      if (formParams.id) {
        updateOutboundEdiMessageType({ ...commonOutboundParams, id: formParams.id })
      } else {
        createOutboundEdiMessageType({
          ...commonOutboundParams,
          outboundEdiMapperConnectionConfigId: formParams.connectionConfigId,
        })
      }
    }
  }

  return {
    buildFormValues,
    persistEdiMessageType,
    createInboundEdiMessageType,
    updateInboundEdiMessageType,
    createOutboundEdiMessageType,
    updateOutboundEdiMessageType,
  }
}
